import _chunk from 'lodash/chunk'
import { each as eachPromise } from 'bluebird'
import {
  CREATE_TURN_ON_AUTO_PAY_IN_BATCH,
  CREATE_USER_CANCEL_SUBSCRIPTIONS_IN_BATCH,
  CREATE_USER_HANDLE_CHARGEBACKS,
  CREATE_USER_HANDLE_INVOICES_POST,
  setSubscriptionCancelsInBatchData,
  setSubscriptionCancelsInBatchError,
  setSubscriptionCancelsInBatchProcessing,
  setSubscriptionCancelsInBatchProgress,
  setTurnOnAutoPayInBatchData,
  setTurnOnAutoPayInBatchError,
  setTurnOnAutoPayInBatchProcessing,
  setTurnOnAutoPayInBatchProgress,
  setUserHandleChargebacksData,
  setUserHandleChargebacksError,
  setUserHandleChargebacksProcessing,
  setUserHandleChargebacksProgress,
  setUserHandleInvoicesPostData,
  setUserHandleInvoicesPostError,
  setUserHandleInvoicesPostProcessing,
  setUserHandleInvoicesPostProgress,
} from './redux/actions'
import {
  STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
  STORE_KEY_FINANCE_HANDLE_CHARGEBACK,
  STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH,
  STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
} from '../store-keys'
import {
  createTurnOnAutoPayInBatch,
  createUserHandleChargebacks,
  createUserSubscriptionCancelationsInBatch,
  createUserHandleInvoicesPost,
} from "."

const log = console
const CANCELATION_CHUNK_SIZE = 20
const TURN_ON_AUTO_PAY_CHUNK_SIZE = 20
const CHARGEBACKS_CHUNK_SIZE = 5
const INVOICES_CHUNK_SIZE = 20

/**
 * Set finance watcher that will process chargebacks
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function createUserHandleChargeback ({ before } = {}) {
  return before(CREATE_USER_HANDLE_CHARGEBACKS, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const { paymentNumbers } = action.payload
    dispatch(setUserHandleChargebacksProcessing({ storeKey: STORE_KEY_FINANCE_HANDLE_CHARGEBACK }))

    const successResults = []
    const failedResults = []
    const paymentNumbersChunks = _chunk(paymentNumbers, CHARGEBACKS_CHUNK_SIZE)
    const chunksCount = paymentNumbersChunks.length
    let count = 1
    await eachPromise(paymentNumbersChunks, async (paymentNumbersChunk) => {
      dispatch(setUserHandleChargebacksProgress({
        storeKey: STORE_KEY_FINANCE_HANDLE_CHARGEBACK,
        progress: `Processing chargebacks in batches of ${CHARGEBACKS_CHUNK_SIZE}. Running batch ${count} of ${chunksCount}`,
      }))
      const options = { auth, server, paymentNumbers: paymentNumbersChunk }
      try {
        const paymentNumberData = await createUserHandleChargebacks(options)
        Object.keys(paymentNumberData).forEach((key) => {
          const chargebackResult = paymentNumberData[key]
          if (chargebackResult.success) {
            successResults.push({ paymentNumber: key })
          } else {
            failedResults.push({ paymentNumber: key, message: chargebackResult.errors.join(', ') })
          }
        })
      } catch (error) {
        log.error(error, error.message)
        paymentNumbersChunk.forEach((paymentNumber) => {
          failedResults.push({ paymentNumber, message: error.message })
        })
      }
      count += 1
    })
    dispatch(setUserHandleChargebacksError({
      storeKey: STORE_KEY_FINANCE_HANDLE_CHARGEBACK,
      error: failedResults,
    }))
    dispatch(setUserHandleChargebacksData({
      storeKey: STORE_KEY_FINANCE_HANDLE_CHARGEBACK,
      data: successResults,
    }))
  })
    .catch((error) => {
      const message = 'Error processing chargebacks'
      log.error(error, message)
      return setUserHandleChargebacksError({
        storeKey: STORE_KEY_FINANCE_HANDLE_CHARGEBACK,
        error: message,
      })
    })
}

/**
 * Set finance watcher that will process invoices
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function createUserHandleInvoicePost ({ before } = {}) {
  return before(CREATE_USER_HANDLE_INVOICES_POST, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const { invoiceIds } = action.payload
    dispatch(setUserHandleInvoicesPostProcessing({
      storeKey: STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
    }))

    const successResults = []
    const failedResults = []
    const invoicesChunks = _chunk(invoiceIds, INVOICES_CHUNK_SIZE)
    const chunksCount = invoicesChunks.length
    let count = 1
    await eachPromise(invoicesChunks, async (invoicesChunk) => {
      dispatch(setUserHandleInvoicesPostProgress({
        storeKey: STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
        progress: `Processing invoices in batches of ${INVOICES_CHUNK_SIZE}. Running batch ${count} of ${chunksCount}`,
      }))
      const options = { auth, server, invoiceIds: invoicesChunk }
      try {
        const invoiceData = await createUserHandleInvoicesPost(options)
        invoiceData.forEach((invoice) => {
          if (invoice.success) {
            successResults.push({ invoiceId: invoice.invoiceId })
          } else {
            failedResults.push({ invoiceId: invoice.invoiceId, message: invoice.error })
          }
        })
      } catch (error) {
        log.error(error, error.message)
        invoicesChunks.forEach((invoiceId) => {
          failedResults.push({ invoiceId, message: error.message })
        })
      }
      count += 1
    })
    dispatch(setUserHandleInvoicesPostError({
      storeKey: STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
      error: failedResults,
    }))
    dispatch(setUserHandleInvoicesPostData({
      storeKey: STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
      data: successResults,
    }))
  })
    .catch((error) => {
      const message = 'Error processing invoices'
      log.error(error, message)
      return setUserHandleInvoicesPostError({
        storeKey: STORE_KEY_FINANCE_POST_INVOICES_IN_BATCH,
        error: message,
      })
    })
}

/**
 * Set finance watcher that will process subscription cancels in batch
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function createUserCancelSubscriptionsInBatch ({ before } = {}) {
  return before(CREATE_USER_CANCEL_SUBSCRIPTIONS_IN_BATCH, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const { cancels } = action.payload
    dispatch(setSubscriptionCancelsInBatchProcessing({
      storeKey: STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
    }))

    const successResults = []
    const failedResults = []
    const cancelsChunks = _chunk(cancels, CANCELATION_CHUNK_SIZE)
    const chunksCount = cancelsChunks.length
    let count = 1
    await eachPromise(cancelsChunks, async (cancelsChunk) => {
      dispatch(setSubscriptionCancelsInBatchProgress({
        storeKey: STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
        progress: `Canceling accounts in batches of ${CANCELATION_CHUNK_SIZE}. Running batch ${count} of ${chunksCount}`,
      }))
      const options = { auth, server, cancels: cancelsChunk }
      try {
        const cancelsData = await createUserSubscriptionCancelationsInBatch(options)
        Object.keys(cancelsData).forEach((key) => {
          const cancelResult = cancelsData[key]
          if (cancelResult.success) {
            successResults.push({ accountId: key })
          } else {
            failedResults.push({ accountId: key, message: cancelResult.errors.join(', ') })
          }
        })
      } catch (error) {
        log.error(error, error.message)
        cancelsChunk.forEach((cancel) => {
          failedResults.push({ accountId: cancel.accountId, message: error.message })
        })
      }
      count += 1
    })

    dispatch(setSubscriptionCancelsInBatchError({
      storeKey: STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
      error: failedResults,
    }))
    dispatch(setSubscriptionCancelsInBatchData({
      storeKey: STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
      data: successResults,
    }))
  })
    .catch((error) => {
      const message = 'Error processing cancels'
      log.error(error, message)
      return setSubscriptionCancelsInBatchError({
        storeKey: STORE_KEY_FINANCE_CANCEL_SUBSCRIPTIONS_IN_BATCH,
        error: message,
      })
    })
}

/**
 * Set finance watcher that will turn on auto pay in batch
 * @param {Object} param0 The parameters
 * @param {Function} param0.before The witness take every function
 */
export function createUserTurnOnAutoPayInBatch ({ before } = {}) {
  return before(CREATE_TURN_ON_AUTO_PAY_IN_BATCH, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const server = config.getIn(['data', 'servers', 'brooklyn'])
    if (!auth.getIn(['data', 'jwt']) || !server) {
      return
    }

    const { accounts } = action.payload
    dispatch(
      setTurnOnAutoPayInBatchProcessing({ storeKey: STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH }),
    )

    const successResults = []
    const failedResults = []
    const autoPayChunks = _chunk(accounts, TURN_ON_AUTO_PAY_CHUNK_SIZE)
    const chunksCount = autoPayChunks.length
    let count = 1
    await eachPromise(autoPayChunks, async (autoPayChunk) => {
      dispatch(setTurnOnAutoPayInBatchProgress({
        storeKey: STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH,
        progress: `Turning on auto pay for accounts in batches of ${TURN_ON_AUTO_PAY_CHUNK_SIZE}. Running batch ${count} of ${chunksCount}`,
      }))
      const options = { auth, server, accounts: autoPayChunk }
      try {
        const autoPayResponse = await createTurnOnAutoPayInBatch(options)
        Object.keys(autoPayResponse).forEach((key) => {
          const autoPayResult = autoPayResponse[key]
          if (autoPayResult.success) {
            successResults.push({ accountId: key })
          } else {
            failedResults.push({ accountId: key, message: autoPayResult.errors.join(', ') })
          }
        })
      } catch (error) {
        log.error(error, error.message)
        autoPayChunk.forEach((account) => {
          failedResults.push({ accountId: account.accountId, message: error.message })
        })
      }
      count += 1
    })
    dispatch(setTurnOnAutoPayInBatchError({
      storeKey: STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH,
      error: failedResults,
    }))
    dispatch(setTurnOnAutoPayInBatchData({
      storeKey: STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH,
      data: successResults,
    }))
  })
    .catch((error) => {
      const message = 'Error turning on auto pay on in batches'
      log.error(error, message)
      return setTurnOnAutoPayInBatchError({
        storeKey: STORE_KEY_FINANCE_TURN_ON_AUTO_PAY_IN_BATCH,
        error: message,
      })
    })
}
